<template>
	<div class="header_profitability_analysis is-full-width px-5 py-2">
		<p class="title">Análisis de Categorías</p>

		<!-- Un filtro por categoría, el mismo desplegable de "Filtrar cuentas"
			de Ventas/Costos: abre sin nada tildado (= todos los ítems), muestra
			la cuenta de tildados y "Ver todos" para volver al conjunto completo.
			La principal (la del análisis) se distingue con su propio estilo. -->
		<div v-if="categories.length > 0" class="category_filters is-flex is-align-items-center">
			<MultiSelectDropdown
				v-for="cat in categories"
				:key="cat.id"
				class="category_select"
				:class="{ 'is-principal': cat.id === category }"
				:value="selectedItems[cat.id] || []"
				:options="optionsOf(cat)"
				:placeholder="cat.label"
				searchable
				@open="onCategoryOpen(cat)"
				@input="(values) => onCategoryChange(cat.id, values)" />
		</div>

		<div class="right_cluster is-flex is-align-items-center">
			<!-- Granularidad del período analizado: el calendario elige el
				punto focal y este selector el bucket que lo contiene (el mes,
				su trimestre o su año). Todo el drawer se recalcula. -->
			<div class="period_switch" role="group" aria-label="Granularidad del período">
				<button v-for="option in periodOptions" :key="option.value"
					type="button"
					class="period_btn"
					:class="{ 'is-active': period === option.value }"
					:aria-pressed="String(period === option.value)"
					@click="onPeriodChange(option.value)">
					{{ option.label }}
				</button>
			</div>

			<div v-if="endMonth" class="calendar_picker is-flex is-align-items-center ml-3">
				<AnualCalendar
					:value="endMonth"
					:disableNewerDates="true"
					:disabledFrom="getNow"
					:formatLabelDate="calendarLabelFormat"
					:alignRight="true"
					@date-change="onDateChange" />
			</div>

			<ReportExportButton class="ml-3 drawer_export" />

			<button class="close_btn ml-3" aria-label="Cerrar modal" @click="$emit('close-modal')">
				<Ecs class="ecs" />
			</button>
		</div>
	</div>
</template>

<script>
import { mapState } from "vuex";
import Ecs from "@/components/svg/Ecs.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import AnualCalendar from "@/components/classification/AnualCalendar.vue";
import ReportExportButton from "@/components/financialReports/ReportExportButton.vue";

// Header del drawer "Análisis de Categorías" (Rentabilidad). Mismo cluster que
// el de Costos: título, un filtro por categoría configurada, mes, Exportar y
// cerrar. Diferencia propia: el filtro con el que se interactúa pasa a ser la
// categoría PRINCIPAL del análisis (la de las cards y la evolución), no sólo
// un filtro de ítems — así se pasa de Tiendas a Marcas sin cerrar el drawer.
// Basta ABRIR el desplegable de otra para que pase a ser la principal
// (`open`), sin nada tildado: ninguna selección = todos los ítems (Steve,
// 2026-10-01). Tildar acota; "Ver todos" del propio desplegable vuelve a
// vaciar la selección.
//
// El cluster derecho suma el selector de granularidad (Mensual / Trimestral /
// Anual): el bucket analizado es el que contiene el mes del calendario, cuya
// etiqueta pasa a mostrar ese bucket ("T2 2026", "2026").
const PERIOD_OPTIONS = [
	{ value: "month", label: "Mensual" },
	{ value: "quarter", label: "Trimestral" },
	{ value: "year", label: "Anual" },
];
// Formato del trigger del calendario por granularidad (moment).
const CALENDAR_LABEL_FORMATS = {
	month: "MMMM YYYY",
	quarter: "[T]Q YYYY",
	year: "YYYY",
};

export default {
	name: "HeaderProfitabilityAnalysis",
	components: { Ecs, MultiSelectDropdown, AnualCalendar, ReportExportButton },
	props: {
		// Shape esperado por ModalFullHeightRight.headerProps:
		// - periodLabel: String — sólo se usa si endMonth no viene
		// - endMonth: Date — mes focal del drawer; alimenta AnualCalendar
		// - getNow: Date — cota superior (mes actual) para deshabilitar futuro
		// - categories: Array<{ id, label, options: [{value, label}] }>
		payload: {
			type: Object,
			default: () => ({
				periodLabel: "",
				endMonth: null,
				getNow: null,
				categories: [],
			}),
		},
	},
	computed: {
		// Del store para que el header reaccione en vivo: `category` marca la
		// principal y `selectedItems` alimenta cada desplegable (vacío = todos).
		...mapState("profitabilityAnalysis", ["category", "selectedItems", "period"]),
		periodOptions() {
			return PERIOD_OPTIONS;
		},
		calendarLabelFormat() {
			return CALENDAR_LABEL_FORMATS[this.period] || CALENDAR_LABEL_FORMATS.month;
		},
		periodLabel() {
			return this.payload?.periodLabel ?? "";
		},
		endMonth() {
			return this.payload?.endMonth ?? null;
		},
		getNow() {
			return this.payload?.getNow ?? null;
		},
		categories() {
			return this.payload?.categories ?? [];
		},
	},
	methods: {
		onDateChange(date) {
			this.$emit("update-end-month", date);
		},
		// La granularidad activa no se re-emite: no hay nada que recalcular.
		onPeriodChange(period) {
			if (period === this.period) return;
			this.$emit("period-change", period);
		},
		// Opciones del desplegable: `{ id, label }` (el ítem es su propio id).
		optionsOf(cat) {
			return (cat.options || []).map((option) => ({
				id: option.value, label: option.label,
			}));
		},
		onCategoryChange(id, values) {
			this.$emit("category-change", { id, values });
		},
		// Abrir el desplegable de OTRA categoría ya la vuelve principal, sin
		// nada tildado (= todos sus ítems). Sobre la principal no se emite nada
		// (abrir no es cambiar).
		onCategoryOpen(cat) {
			if (cat.id === this.category) return;
			this.$emit("category-change", { id: cat.id, values: [] });
		},
	},
};
</script>

<style scoped lang="scss">
// Grid 1fr / auto / 1fr: los selects se anclan al centro real del header y, sin
// categorías, el cluster derecho conserva su columna sin reflow.
.header_profitability_analysis {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 16px;
}

.title {
	grid-column: 1;
	color: var(--report-text);
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.4px;
	margin: 0;
}

.category_filters {
	grid-column: 2;
	gap: 8px;
}

// La categoría principal (la del análisis) se distingue aunque no tenga nada
// tildado, con el mismo relleno que el desplegable usa al tener ítems
// tildados (`.ms_trigger.is-active`): fondo primario del tema y texto claro
// — el verde oscuro con letras blancas que ya pintaba el select anterior.
.category_select.is-principal :deep(.ms_trigger) {
	border-color: var(--report-primary);
	background: var(--report-primary);
	color: var(--report-bg);
}

.right_cluster {
	grid-column: 3;
	justify-self: end;
}

.calendar_picker {
	border-radius: 8px;
}

// Segmented control, el mismo de la sección de detalle (mode_switch).
.period_switch {
	display: inline-flex;
	padding: 2px;
	gap: 2px;
	background: var(--report-surface);
	border: 1px solid var(--report-border);
	border-radius: 999px;
}

.period_btn {
	border: 0;
	border-radius: 999px;
	padding: 6px 12px;
	background: none;
	font-family: inherit;
	font-size: 12px;
	font-weight: 600;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		color: var(--report-text);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 1px;
	}

	&.is-active {
		background: var(--report-bg);
		color: var(--report-accent-text);
		box-shadow: var(--report-shadow-card);
	}
}

// Botón compartido del export (decisiones §34): trae su propio estilo;
// acá solo se iguala la altura con el resto de los controles del header.
.drawer_export.export_btn {
	height: 46px;
}

.close_btn {
	width: 36px;
	height: 36px;
	border-radius: 8px;
	border: 1px solid var(--report-border-soft);
	background: var(--report-bg);
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	box-sizing: border-box;
	overflow: hidden;
	padding-top: 5px;
	cursor: pointer;

	.ecs {
		width: 100%;
		position: relative;
		height: 20px;
		overflow: hidden;
		flex-shrink: 0;
	}
}
</style>
